/* eslint-disable no-magic-numbers */
import { h, ComponentChildren, RefObject, JSX } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";

import { Keyboard } from "../../../foundation/enums";
import CSSProperties from "../../../foundation/model/CSSProperties";
import classname from "../../../foundation/utils/classname";
import { stopPropagation } from "../../../foundation/utils/legacy";
import { clamp } from "../../../foundation/utils/math";

import IconCloseSmall from "../Icons/IconCloseSmall";
import PortalComponent from "../../Portal";

import styles from "./Popover.module.css";

enum Position {
    Top = "top",
    Bottom = "bottom",
    Left = "left",
    Right = "right"
}

enum Alignment {
    Start = "start",
    Center = "center",
    End = "end"
}

enum Mode {
    Relative = "relative",
    Portal = "portal"
}

type PortalOptions = {
    targetRef?: RefObject<HTMLElement>;
    portalInto?: HTMLElement;
    hideArrowOnTargetOutOfView?: boolean;
};

type ScrollContainerPadding = {
    top: number;
    bottom: number;
    left: number;
    right: number;
};

type ScrollContainerOptions = {
    ref?: RefObject<HTMLElement>;
    padding?: Partial<ScrollContainerPadding> | number;
};

type ArrowOptions = {
    width?: number;
    height?: number;
};

type Bounds = Pick<DOMRect, "top" | "left" | "bottom" | "right">;

type OverlayDivProps = JSX.HTMLAttributes<HTMLDivElement> & {
    onClick?: (event: Event) => void;
};

// Specs
const POPOVER_DEFAULT_OFFSET = 12; // px;
const ARROW_DEFAULT_WIDTH = 14; // px - diagonal width of the arrow;
const ARROW_DEFAULT_HEIGHT = 16; // px - diagonal height of the arrow;
const ARROW_MIN_SPACING = 8; // px - space between the side of popover and the arrow;

// Position check
const POSITION_CHECK_INTERVAL = 500; // ms;
const POSITION_CHECK_UNTIL = 5000; // ms;

interface Props {
    class?: string;
    style?: CSSProperties;
    open?: boolean;
    position?: Position;
    alignment?: Alignment;
    mode?: Mode;
    offset?: number;
    // Popover content
    content?: ComponentChildren;
    contentClass?: string;
    contentStyle?: CSSProperties;
    textClass?: string;
    arrowClass?: string;
    // Options
    portalOptions?: PortalOptions;
    scrollContainerOptions?: ScrollContainerOptions;
    arrowOptions?: ArrowOptions;
    overlayDivProps?: OverlayDivProps;
    // Flags
    hasOverlay?: boolean;
    allowHorizontalOverflow?: boolean;
    allowVerticalOverflow?: boolean;
    hideArrow?: boolean;
    showCloseButton?: boolean;
    closeOnDocumentClick?: boolean;
    closeOnEscape?: boolean;
    // Target content
    children?: ComponentChildren;
    // Callbacks
    onClose?: () => void;
}

function getWindowBounds(): Bounds {
    return {
        top: 0,
        left: 0,
        bottom: window.innerHeight,
        right: window.innerWidth
    };
}

const SCROLL_CONTAINER_PADDING_DEFAULT: ScrollContainerPadding = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
};

function Popover({
    class: cssClass,
    style: popoverStyle,
    open = false,
    position: positionProp = Position.Top,
    alignment: alignmentProp = Alignment.Center,
    mode = Mode.Relative,
    offset: popoverOffset = POPOVER_DEFAULT_OFFSET,
    // Popover content
    content,
    contentClass,
    contentStyle,
    textClass = "body-1",
    arrowClass,
    // Options
    portalOptions: {
        targetRef: portalTargetRef,
        portalInto,
        hideArrowOnTargetOutOfView = true
    } = {},
    scrollContainerOptions: {
        ref: scrollContainerRef,
        padding: scrollContainerPaddingProp
    } = {},
    arrowOptions: {
        width: arrowWidth = ARROW_DEFAULT_WIDTH,
        height: arrowHeight = ARROW_DEFAULT_HEIGHT
    } = {},
    overlayDivProps = {},
    // Flags
    hasOverlay = false,
    allowVerticalOverflow = false,
    allowHorizontalOverflow = false,
    hideArrow = false,
    showCloseButton = false,
    closeOnDocumentClick = true,
    closeOnEscape = true,
    // Target content
    children,
    // Callbacks
    onClose
}: Props) {
    const [position, setPosition] = useState<Position>(positionProp);
    const [alignment, setAlignment] = useState<Alignment>(alignmentProp);
    const [popoverTransform, setPopoverTransform] = useState<string>("");
    const [arrowTransform, setArrowTransform] = useState<string>("");

    const popoverRef = useRef<HTMLDivElement | null>();
    const relativeTargetRef = useRef<HTMLDivElement | null>();
    const popoverContentRef = useRef<HTMLDivElement | null>();

    const positionCheckTimePassed = useRef<number>(0);

    const clickOnPopover = useRef<boolean>(false);
    const clickOnTarget = useRef<boolean>(false);

    let paddingProp = (scrollContainerPaddingProp && typeof scrollContainerPaddingProp === "object")
        ? scrollContainerPaddingProp
        : {};

    if (scrollContainerPaddingProp && typeof scrollContainerPaddingProp === "number") {
        paddingProp = {
            top: scrollContainerPaddingProp,
            bottom: scrollContainerPaddingProp,
            left: scrollContainerPaddingProp,
            right: scrollContainerPaddingProp
        };
    }

    const scrollContainerPadding = {
        ...SCROLL_CONTAINER_PADDING_DEFAULT,
        ...paddingProp
    };

    const arrowSideLength = Math.sqrt((arrowWidth / 2) ** 2 + (arrowHeight / 2) ** 2); // px;

    const ARROW_CALCULATED_SIDE_LENGTH = 2 * Math.sqrt((arrowSideLength ** 2) / 2); // This is the side length of the arrow when it is rotated by 45 degrees
    const ARROW_POSITION_OFFSET = (ARROW_CALCULATED_SIDE_LENGTH - arrowSideLength) / 2; // This is the offset that comes from the arrow's rotational transformation

    function handleOverlayClick(event: MouseEvent) {
        event.stopPropagation();
        onClose?.();
    }

    function handleDocumentClick() {
        if (clickOnPopover.current || clickOnTarget.current) {
            return;
        }

        onClose?.();
    }

    function handleDocumentMouseUp() {
        // By default, document mouse up event is fired before document click
        // Here, we want to change this order
        // Now, document mouse up is fired after document click to cleanup the state
        setTimeout(() => {
            clickOnPopover.current = false;
            clickOnTarget.current = false;
        });
    }

    function handlePopoverMouseDown(event: MouseEvent) {
        event.stopPropagation();
        clickOnPopover.current = true;
    }

    function handlePopoverMouseUp() {
        clickOnPopover.current = false;
    }

    function handleTargetMouseDown() {
        clickOnTarget.current = true;
    }

    function handleTargetMouseUp() {
        // By default, target mouse up event is fired before document click
        // Here, we want to change this order
        // Now, target mouse up is fired after document click to cleanup the state
        setTimeout(() => {
            clickOnTarget.current = false;
        });
    }

    // Position the arrow relative to the popover, when there is no relative target and when mode is not portal
    useLayoutEffect(() => {
        if (!open || hideArrow || !popoverRef.current || mode === Mode.Portal || relativeTargetRef.current) {
            return;
        }

        function getArrowTransform(popoverPosition: Position, popoverAlignment: Alignment) {
            const popoverRect = popoverRef.current!.getBoundingClientRect();

            let arrowX = 0;
            let arrowY = 0;
            let rotate = 0;

            if (popoverPosition === Position.Top || popoverPosition === Position.Bottom) {
                const positionCoefficient = popoverPosition === Position.Top ? 1 : -1;
                arrowY = positionCoefficient * (arrowSideLength / 2);

                const offsetPercentage = 10;
                if (popoverAlignment === Alignment.Start) {
                    arrowX = popoverRect.width * (offsetPercentage / 100) + arrowWidth / 2;
                } else if (popoverAlignment === Alignment.Center) {
                    arrowX = (popoverRect.width - arrowSideLength) / 2;
                } else if (popoverAlignment === Alignment.End) {
                    arrowX = popoverRect.width * (1 - offsetPercentage / 100) - arrowWidth;
                }

                rotate = popoverPosition === Position.Top ? 45 : 225;
            }

            if (popoverPosition === Position.Left || popoverPosition === Position.Right) {
                const positionCoefficient = popoverPosition === Position.Left ? 1 : -1;
                arrowX = positionCoefficient * (arrowSideLength / 2);

                const offsetPercentage = 10;
                if (popoverAlignment === Alignment.Start) {
                    arrowY = popoverRect.height * (offsetPercentage / 100) + arrowWidth / 2;
                } else if (popoverAlignment === Alignment.Center) {
                    arrowY = (popoverRect.height - arrowSideLength) / 2;
                } else if (popoverAlignment === Alignment.End) {
                    arrowY = popoverRect.height * (1 - offsetPercentage / 100) - arrowWidth;
                }

                rotate = popoverPosition === Position.Left ? 315 : 135;
            }

            return `translate(${arrowX}px, ${arrowY}px) rotate(${rotate}deg)`;
        }

        setArrowTransform(getArrowTransform(position, alignment));
    }, [open, mode, positionProp, alignmentProp, hideArrow, content, position, alignment]);

    // Position popover relative to the target
    useLayoutEffect(() => {
        function getPopoverRect() {
            return popoverRef.current?.getBoundingClientRect();
        }

        function getTargetRect() {
            if (mode === Mode.Portal) {
                return portalTargetRef?.current?.getBoundingClientRect();
            }

            return relativeTargetRef.current?.getBoundingClientRect();
        }

        function getContainerBounds(): Bounds {
            const windowBounds = getWindowBounds();

            if (!scrollContainerRef?.current) {
                return {
                    top: windowBounds.top + scrollContainerPadding.top,
                    left: windowBounds.left + scrollContainerPadding.left,
                    bottom: windowBounds.bottom - scrollContainerPadding.bottom,
                    right: windowBounds.right - scrollContainerPadding.right
                };
            }

            const containerRect = scrollContainerRef.current.getBoundingClientRect();

            return {
                top: containerRect.top + scrollContainerPadding.top,
                left: containerRect.left + scrollContainerPadding.left,
                bottom: Math.min(containerRect.bottom - scrollContainerPadding.bottom, windowBounds.bottom),
                right: Math.min(containerRect.right - scrollContainerPadding.right, windowBounds.right)
            };
        }

        function canCalculateTransform() {
            return !!getPopoverRect() && !!getTargetRect();
        }

        if (!canCalculateTransform()) {
            return;
        }

        const portalTargetResizeObserver = new ResizeObserver(updateTransform);
        const popoverContentResizeObserver = new ResizeObserver(updateTransform);

        let positionCheckIntervalId: ReturnType<typeof setTimeout> | undefined;

        // Top difference between target and popover when positioned correctly
        function getTopDifference(popoverPosition: Position, popoverAlignment: Alignment) {
            const targetRect = getTargetRect()!;
            const popoverRect = getPopoverRect()!;

            if (popoverPosition === Position.Top) {
                return popoverRect.height + popoverOffset;
            }

            if (popoverPosition === Position.Bottom) {
                return targetRect.height + popoverOffset;
            }

            // Below, position is Left or Right

            if (popoverAlignment === Alignment.Start) {
                return 0;
            }

            if (popoverAlignment === Alignment.Center) {
                return (popoverRect.height - targetRect.height) / 2;
            }

            if (popoverAlignment === Alignment.End) {
                return popoverRect.height - targetRect.height;
            }

            return 0;
        }

        // Bottom difference between target and popover when positioned correctly
        function getBottomDifference(popoverPosition: Position, popoverAlignment: Alignment) {
            const targetRect = getTargetRect()!;
            const popoverRect = getPopoverRect()!;

            if (popoverPosition === Position.Top) {
                return targetRect.height + popoverOffset;
            }

            if (popoverPosition === Position.Bottom) {
                return popoverRect.height + popoverOffset;
            }

            // Below, position is Left or Right

            if (popoverAlignment === Alignment.Start) {
                return popoverRect.height - targetRect.height;
            }

            if (popoverAlignment === Alignment.Center) {
                return (popoverRect.height - targetRect.height) / 2;
            }

            if (popoverAlignment === Alignment.End) {
                return 0;
            }

            return 0;
        }

        // Left difference between target and popover when positioned correctly
        function getLeftDifference(popoverPosition: Position, popoverAlignment: Alignment) {
            const targetRect = getTargetRect()!;
            const popoverRect = getPopoverRect()!;

            if (popoverPosition === Position.Left) {
                return popoverRect.width + popoverOffset;
            }

            if (popoverPosition === Position.Right) {
                return targetRect.width + popoverOffset;
            }

            // Below, position is Top or Bottom

            if (popoverAlignment === Alignment.Start) {
                return 0;
            }

            if (popoverAlignment === Alignment.Center) {
                return (popoverRect.width - targetRect.width) / 2;
            }

            if (popoverAlignment === Alignment.End) {
                return popoverRect.width - targetRect.width;
            }

            return 0;
        }

        // Right difference between target and popover when positioned correctly
        function getRightDifference(popoverPosition: Position, popoverAlignment: Alignment) {
            const targetRect = getTargetRect()!;
            const popoverRect = getPopoverRect()!;

            if (popoverPosition === Position.Left) {
                return targetRect.width + popoverOffset;
            }

            if (popoverPosition === Position.Right) {
                return popoverRect.width + popoverOffset;
            }

            // Below, position is Top or Bottom

            if (popoverAlignment === Alignment.Start) {
                return popoverRect.width - targetRect.width;
            }

            if (popoverAlignment === Alignment.Center) {
                return (popoverRect.width - targetRect.width) / 2;
            }

            if (popoverAlignment === Alignment.End) {
                return 0;
            }

            return 0;
        }

        function getPopoverTransform(popoverPosition: Position, popoverAlignment: Alignment) {
            const targetRect = getTargetRect()!;
            const popoverRect = getPopoverRect()!;

            const topDifference = getTopDifference(popoverPosition, popoverAlignment);
            const leftDifference = getLeftDifference(popoverPosition, popoverAlignment);

            let popoverX = mode === Mode.Portal ? targetRect.left : 0;
            let popoverY = mode === Mode.Portal ? targetRect.top : 0;

            if (popoverPosition === Position.Top || popoverPosition === Position.Bottom) {
                popoverX += -1 * leftDifference;

                if (popoverPosition === Position.Top) {
                    popoverY += -1 * (popoverRect.height + popoverOffset);
                }

                if (popoverPosition === Position.Bottom) {
                    popoverY += targetRect.height + popoverOffset;
                }
            }

            if (popoverPosition === Position.Left || popoverPosition === Position.Right) {
                popoverY += -1 * topDifference;

                if (popoverPosition === Position.Left) {
                    popoverX += -1 * (popoverRect.width + popoverOffset);
                }

                if (popoverPosition === Position.Right) {
                    popoverX += targetRect.width + popoverOffset;
                }
            }

            if (mode === Mode.Portal) {
                const containerBounds = getContainerBounds();
                const minPopoverX = containerBounds.left;
                const minPopoverY = containerBounds.top;
                const maxPopoverX = containerBounds.right - popoverRect.width;
                const maxPopoverY = containerBounds.bottom - popoverRect.height;

                if (!allowVerticalOverflow) {
                    popoverY = clamp(popoverY, minPopoverY, maxPopoverY);
                }

                if (!allowHorizontalOverflow) {
                    popoverX = clamp(popoverX, minPopoverX, maxPopoverX);
                }
            }

            return `translate(${popoverX}px, ${popoverY}px)`;
        }

        // eslint-disable-next-line complexity
        function getArrowTransform(popoverPosition: Position) {
            const popoverRect = getPopoverRect()!;
            const targetRect = getTargetRect()!;
            const containerBounds = getContainerBounds();

            if (mode === Mode.Portal && hideArrowOnTargetOutOfView) {
                const isTargetOutOfViewHorizontal =
                    targetRect.right < containerBounds.left ||
                    targetRect.left > containerBounds.right;

                const isTargetOutOfViewVertical =
                    targetRect.bottom < containerBounds.top ||
                    targetRect.top > containerBounds.bottom;

                const isTargetOutOfView = isTargetOutOfViewHorizontal || isTargetOutOfViewVertical;

                if (isTargetOutOfView) {
                    return `scale(0)`;
                }
            }

            // These are differences between target and popover, ideally, when they are positioned correctly
            const idealTopDifference = getTopDifference(position, alignment);
            const idealLeftDifference = getLeftDifference(position, alignment);
            const idealRightDifference = getRightDifference(position, alignment);
            const idealBottomDifference = getBottomDifference(position, alignment);

            // These are the top & left differences between target and popover, ideally, when they are positioned correctly
            let topDifference = idealTopDifference;
            let leftDifference = idealLeftDifference;

            if (mode === Mode.Portal) {
                // Useful for position: Top & Bottom
                if (!allowHorizontalOverflow) {
                    const leftOffset = targetRect.left - idealLeftDifference - containerBounds.left;
                    if (leftOffset < 0) {
                        leftDifference += leftOffset;
                    }

                    const rightOffset = targetRect.right + idealRightDifference - containerBounds.right;
                    if (rightOffset > 0) {
                        leftDifference += rightOffset;
                    }
                }

                // Useful for position: Left & Right
                if (!allowVerticalOverflow) {
                    const topOffset = targetRect.top - idealTopDifference - containerBounds.top;
                    if (topOffset < 0) {
                        topDifference += topOffset;
                    }

                    const bottomOffset = targetRect.bottom + idealBottomDifference - containerBounds.bottom;
                    if (bottomOffset > 0) {
                        topDifference += bottomOffset;
                    }
                }
            }

            const arrowHorizontalOffset = ARROW_MIN_SPACING;
            const arrowVerticalOffset = ARROW_MIN_SPACING;

            // Useful for position: Top & Bottom
            const minArrowX = ARROW_POSITION_OFFSET + arrowHorizontalOffset;
            const maxArrowX = popoverRect.width - arrowSideLength - ARROW_POSITION_OFFSET - arrowHorizontalOffset;

            // Useful for position: Left & Right
            const minArrowY = ARROW_POSITION_OFFSET + arrowVerticalOffset;
            const maxArrowY = popoverRect.height - arrowSideLength - ARROW_POSITION_OFFSET - arrowVerticalOffset;

            let arrowX = 0;
            let arrowY = 0;
            let rotate = 0;
            let scale = 1;

            if (popoverPosition === Position.Top || popoverPosition === Position.Bottom) {
                const positionCoefficient = popoverPosition === Position.Top ? 1 : -1;
                rotate = popoverPosition === Position.Top ? 45 : 225;
                arrowX = leftDifference + (targetRect.width - arrowWidth) / 2;
                arrowY = positionCoefficient * (arrowSideLength / 2);

                arrowX = clamp(arrowX, minArrowX, maxArrowX);

                if (mode === Mode.Portal) {
                    const targetVisiblePartOnLeft = targetRect.right - containerBounds.left;

                    const arrowTargetConnectionLostOnLeft = mode === Mode.Portal &&
                        targetVisiblePartOnLeft - arrowSideLength / 2 < minArrowX;

                    const targetVisiblePartOnRight = containerBounds.right - targetRect.left;

                    const arrowTargetConnectionLostOnRight = mode === Mode.Portal &&
                        targetVisiblePartOnRight - arrowSideLength / 2 < minArrowX;

                    // Hide arrow outside when connection between target and arrow is lost
                    if (arrowTargetConnectionLostOnLeft || arrowTargetConnectionLostOnRight) {
                        scale = 0;
                    }
                }
            }

            if (popoverPosition === Position.Left || popoverPosition === Position.Right) {
                const positionCoefficient = popoverPosition === Position.Left ? 1 : -1;
                rotate = popoverPosition === Position.Left ? 315 : 135;
                arrowY = topDifference + (targetRect.height - arrowWidth) / 2;
                arrowX = positionCoefficient * (arrowSideLength / 2);

                arrowY = clamp(arrowY, minArrowY, maxArrowY);

                if (mode === Mode.Portal) {
                    const targetVisiblePartOnTop = targetRect.bottom - containerBounds.top;

                    const arrowTargetConnectionLostOnTop = mode === Mode.Portal &&
                        targetVisiblePartOnTop - arrowSideLength / 2 < minArrowY;

                    const targetVisiblePartOnBottom = containerBounds.bottom - targetRect.top;

                    const arrowTargetConnectionLostOnBottom = mode === Mode.Portal &&
                        targetVisiblePartOnBottom - arrowSideLength / 2 < minArrowY;

                    // Hide arrow outside when connection between target and arrow is lost
                    if (arrowTargetConnectionLostOnTop || arrowTargetConnectionLostOnBottom) {
                        scale = 0;
                    }
                }
            }

            return `translate(${arrowX}px, ${arrowY}px) rotate(${rotate}deg) scale(${scale})`;
        }

        // eslint-disable-next-line complexity
        function calculateOverflowedPositionAlignment(options?: {
            horizontalFlip?: boolean;
            verticalFlip?: boolean;
            horizontalShift?: boolean;
            verticalShift?: boolean;
        }) {
            const {
                horizontalFlip = false,
                verticalFlip = false,
                horizontalShift = false,
                verticalShift = false
            } = options ?? {};

            if (!horizontalFlip && !verticalFlip && !horizontalShift && !verticalShift) {
                return {
                    position: positionProp,
                    alignment: alignmentProp
                };
            }

            const targetRect = getTargetRect()!;
            const containerBounds = getContainerBounds();

            // Differences between target and popover
            const topDifference = getTopDifference(positionProp, alignmentProp);
            const bottomDifference = getBottomDifference(positionProp, alignmentProp);
            const leftDifference = getLeftDifference(positionProp, alignmentProp);
            const rightDifference = getRightDifference(positionProp, alignmentProp);

            // top
            const calculatedPopoverTop = targetRect.top - topDifference;
            const topOverflow = calculatedPopoverTop < containerBounds.top;

            // bottom
            const calculatedPopoverBottom = targetRect.bottom + bottomDifference;
            const bottomOverflow = calculatedPopoverBottom > containerBounds.bottom;

            // left
            const calculatedPopoverLeft = targetRect.left - leftDifference;
            const leftOverflow = calculatedPopoverLeft < containerBounds.left;

            // right
            const calculatedPopoverRight = targetRect.right + rightDifference;
            const rightOverflow = calculatedPopoverRight > containerBounds.right;

            let updatedPosition = position;
            let updatedAlignment = alignment;

            if (horizontalFlip) {
                if (positionProp === Position.Left) {
                    if (leftOverflow) {
                        updatedPosition = Position.Right;
                    } else {
                        updatedPosition = Position.Left;
                    }
                }

                if (positionProp === Position.Right) {
                    if (rightOverflow) {
                        updatedPosition = Position.Left;
                    } else {
                        updatedPosition = Position.Right;
                    }
                }
            }

            if (verticalFlip) {
                if (positionProp === Position.Top) {
                    if (topOverflow) {
                        updatedPosition = Position.Bottom;
                    } else {
                        updatedPosition = Position.Top;
                    }
                }

                if (positionProp === Position.Bottom) {
                    if (bottomOverflow) {
                        updatedPosition = Position.Top;
                    } else {
                        updatedPosition = Position.Bottom;
                    }
                }
            }

            if (horizontalShift && (positionProp === Position.Top || positionProp === Position.Bottom)) {
                if (leftOverflow && leftDifference > 0) {
                    updatedAlignment = Alignment.Start;
                } else if (leftOverflow && rightDifference < 0) {
                    // When target is wider than popover
                    updatedAlignment = Alignment.End;
                } else if (!rightOverflow) {
                    updatedAlignment = alignmentProp;
                }

                if (rightOverflow && rightDifference > 0) {
                    updatedAlignment = Alignment.End;
                } else if (rightOverflow && leftDifference < 0) {
                    // When target is wider than popover
                    updatedAlignment = Alignment.Start;
                } else if (!leftOverflow) {
                    updatedAlignment = alignmentProp;
                }
            }

            if (verticalShift && (positionProp === Position.Left || positionProp === Position.Right)) {
                if (topOverflow && topDifference > 0) {
                    updatedAlignment = Alignment.Start;
                } else if (topOverflow && bottomDifference < 0) {
                    // When target is taller than popover
                    updatedAlignment = Alignment.End;
                } else if (!bottomOverflow) {
                    updatedAlignment = alignmentProp;
                }

                if (bottomOverflow && bottomDifference > 0) {
                    updatedAlignment = Alignment.End;
                } else if (bottomOverflow && topDifference < 0) {
                    // When target is taller than popover
                    updatedAlignment = Alignment.Start;
                } else if (!topOverflow) {
                    updatedAlignment = alignmentProp;
                }
            }

            return {
                position: updatedPosition,
                alignment: updatedAlignment
            };
        }

        function updateTransform() {
            if (!canCalculateTransform()) {
                return;
            }

            const {
                position: calculatedPosition,
                alignment: calculatedAlignment
            } = calculateOverflowedPositionAlignment({
                horizontalFlip: !allowHorizontalOverflow,
                verticalFlip: !allowVerticalOverflow,
                horizontalShift: !allowHorizontalOverflow && mode === Mode.Relative,
                verticalShift: !allowVerticalOverflow && mode === Mode.Relative
            });

            setPosition(calculatedPosition);
            setAlignment(calculatedAlignment);
            setPopoverTransform(getPopoverTransform(calculatedPosition, calculatedAlignment));

            if (!hideArrow) {
                setArrowTransform(getArrowTransform(calculatedPosition));
            }
        }

        // This logic is needed to make sure that the popover is positioned correctly
        // when the target layout is shifted due to some other UI changes during
        // the loading phase of the view
        function executePositionCheck() {
            if (!open || (positionCheckTimePassed.current >= POSITION_CHECK_UNTIL)) {
                clearInterval(positionCheckIntervalId);
                return;
            }

            updateTransform();
            positionCheckTimePassed.current += POSITION_CHECK_INTERVAL;
        }

        function subscribe() {
            if (scrollContainerRef?.current) {
                scrollContainerRef.current.addEventListener("scroll", updateTransform);
            }

            window.addEventListener("resize", updateTransform);
            window.addEventListener("scroll", updateTransform);

            positionCheckIntervalId = setInterval(executePositionCheck, POSITION_CHECK_INTERVAL);

            if (portalTargetRef?.current) {
                portalTargetResizeObserver.observe(portalTargetRef.current);
            }

            if (popoverContentRef?.current) {
                popoverContentResizeObserver.observe(popoverContentRef.current);
            }
        }

        function ubsubscribe() {
            if (scrollContainerRef?.current) {
                scrollContainerRef.current.removeEventListener("scroll", updateTransform);
            }

            window.removeEventListener("resize", updateTransform);
            window.removeEventListener("scroll", updateTransform);

            clearInterval(positionCheckIntervalId);

            if (portalTargetRef?.current) {
                portalTargetResizeObserver.unobserve(portalTargetRef.current);
            }

            if (popoverContentRef?.current) {
                popoverContentResizeObserver.unobserve(popoverContentRef.current);
            }
        }

        if (open) {
            updateTransform();
            subscribe();
        } else {
            ubsubscribe();
        }

        return () => {
            ubsubscribe();
        };
    }, [open, mode, positionProp, alignmentProp, hideArrow,
        popoverOffset, scrollContainerRef, portalTargetRef,
        allowHorizontalOverflow, allowVerticalOverflow, content,
        position, alignment]);

    // Update position when position prop changes
    useEffect(() => {
        setPosition(positionProp);
    }, [positionProp]);

    // Update alignment when alignment prop changes
    useEffect(() => {
        setAlignment(alignmentProp);
    }, [alignmentProp]);

    // Close popover when clicked outside
    useEffect(() => {
        function subscribe() {
            document.addEventListener("mouseup", handleDocumentMouseUp);
            document.addEventListener("click", handleDocumentClick);
        }

        function ubsubscribe() {
            document.removeEventListener("mouseup", handleDocumentMouseUp);
            document.removeEventListener("click", handleDocumentClick);
        }

        if (open && closeOnDocumentClick && !hasOverlay) {
            subscribe();
        } else {
            ubsubscribe();
        }

        return () => {
            ubsubscribe();
        };
    }, [open, closeOnDocumentClick, onClose]);

    // Don't close popover when target is clicked
    useEffect(() => {
        if (!portalTargetRef?.current) {
            return;
        }

        portalTargetRef.current.addEventListener("mousedown", handleTargetMouseDown);
        portalTargetRef.current.addEventListener("mouseup", handleTargetMouseUp);

        return () => {
            portalTargetRef.current?.removeEventListener("mousedown", handleTargetMouseDown);
            portalTargetRef.current?.removeEventListener("mouseup", handleTargetMouseUp);
        };
    }, [portalTargetRef]);

    // Close popover when escape key is pressed
    useEffect(() => {
        function handleEscape(event: KeyboardEvent) {
            if (event.key === Keyboard.Key.Escape) {
                onClose?.();
                event.stopPropagation();
            }
        }

        const addListener = () => document.addEventListener("keydown", handleEscape);
        const removeListener = () => document.removeEventListener("keydown", handleEscape);

        if (open && closeOnEscape) {
            addListener();
        } else {
            removeListener();
        }

        return () => {
            removeListener();
        };
    }, [open, closeOnEscape, onClose]);

    // Reset the click states when popover is closed
    useEffect(() => {
        function cleanupClickState() {
            setTimeout(() => {
                clickOnTarget.current = false;
                clickOnPopover.current = false;
            });
        }

        if (!open) {
            cleanupClickState();
        }

        return () => {
            cleanupClickState();
        };
    }, [open]);

    function renderPopover() {
        if (!open) {
            return null;
        }

        const { class: overlayDivClass, ...restOverlayDivProps } = overlayDivProps;

        const popover = (
            <div
                class={classname(styles.popover, cssClass, styles[position], {
                    [styles.standalone]: mode === Mode.Relative && !children,
                    [styles.portal]: mode === Mode.Portal
                })}
                ref={popoverRef}
                style={{ ...popoverStyle, transform: popoverTransform }}
                onMouseDown={handlePopoverMouseDown}
                onMouseUp={handlePopoverMouseUp}
                onClick={stopPropagation}>
                <div
                    class={classname(styles.popoverContent, contentClass, textClass)}
                    style={contentStyle}
                    ref={popoverContentRef}>
                    {content}
                </div>
                {renderArrow()}
                {showCloseButton && (
                    <div class={styles.closeButton} onClick={onClose}>
                        <IconCloseSmall/>
                    </div>
                )}
            </div>
        );

        if (hasOverlay) {
            return (
                <div>
                    <div
                        class={classname(styles.popoverOverlay, overlayDivClass)}
                        onClick={handleOverlayClick}
                        {...restOverlayDivProps}>
                    </div>
                    {popover}
                </div>
            );
        }

        return popover;
    }

    function renderArrow() {
        if (hideArrow) {
            return null;
        }

        return (
            <div class={classname(styles.arrow, arrowClass)} style={{
                "--arrow-side-length": `${arrowSideLength}px`,
                "transform": arrowTransform
            } as CSSProperties}/>
        );
    }

    function renderRelativeTarget() {
        if (!children) {
            return null;
        }

        return (
            <div ref={relativeTargetRef} onMouseDown={handleTargetMouseDown} onMouseUp={handleTargetMouseUp}>
                {children}
            </div>
        );
    }

    if (mode === Mode.Portal) {
        if (!open) {
            return null;
        }

        return (
            <PortalComponent into={portalInto}>
                {renderPopover()}
            </PortalComponent>
        );
    }

    if (!children) {
        // Standalone popover
        return renderPopover();
    }

    if (!open) {
        return renderRelativeTarget();
    }

    return (
        <div class={styles.popoverContainer}>
            {renderRelativeTarget()}
            {renderPopover()}
        </div>
    );
}

export default Popover;
export {
    Props as PopoverProps,
    Position as PopoverPosition,
    Alignment as PopoverAlignment,
    Mode as PopoverMode
};
